import React, { FC } from 'react';
import { Helmet } from 'react-helmet-async';
import NavigationBarItem from '../@shared/navigationBarItem';
import { NavigationBarModel } from './models/productBreadcrumbBar.model';
import { TNavigationBarProps } from './types/productBreadcrumbBar.types';
import ExperienceEditorComponentPlaceholder from '../@shared/experienceEditorComponentPlaceholder';
import EmptyApiDataMessage from '../@shared/emptyApiDataMessage';
import useExperiencerEditorPlaceholder from '../../hooks/useExperiencerEditorPlaceholder';
import useHeroBanner from '../../hooks/useHeroBanner';
import { isEmptyResponse } from '../../utils/checkEmptyResponse';
import { BreakpointsInPx } from '../../constants/breakpoints.constants';
import useMediaQuery from '../../lib/custom-hooks/useMediaQuery';
import { isEditorActive, withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import RoutableNotEditableLink from '../@shared/routableNotEditableLink';
import Icon from '../@shared/icon';
import UnreleasedComponent from '../@shared/unreleasedComponent';
import { googleClickBreadcrumbEvent } from '../../utils/analytics';

const ProductBreadcrumbBar: FC<TNavigationBarProps> = (props) => {
  const apiResponse = isEmptyResponse(props);
  const model = new NavigationBarModel(props);
  const isPlaceholderShown = useExperiencerEditorPlaceholder(props);
  const { isHeroBannerPresent, textColor: heroBannerTextColor } = useHeroBanner();
  const isMobile = useMediaQuery(`(max-width: ${BreakpointsInPx['md']})`);

  // Component is unreleased
  if (model.isUnreleased) {
    return <UnreleasedComponent />;
  }

  //  star node opened directly in EE
  if (isPlaceholderShown) {
    return (
      <ExperienceEditorComponentPlaceholder
        image={model?.thumbnailEE}
        componentName={model?.componentName}
      />
    );
  }

  // not opened from star node
  // data missing when navigating via from products node
  if (!apiResponse && isEditorActive()) {
    return <EmptyApiDataMessage componentName={model?.componentName} />;
  }

  // No data and not in EE
  if (!apiResponse && !isPlaceholderShown && !isEditorActive()) {
    return null;
  }

  return (
    <>
      <Helmet script={[model.breadcrumbJSONLD]} />
      <nav className={isHeroBannerPresent ? 'nav-bar nav-bar__hero-banner' : 'nav-bar nav-bar__no-hero-banner'}>
        <div className="nav-bar__wrapper">
          <ul className="nav-bar__breadcrumb">
            {model.breadcrumbItems.map((item, i, self) => (
              <NavigationBarItem
                item={item}
                isLast={i === self.length - 1}
                key={`navigation-bar-item-${i}`}
                extraClassName={`nav-bar__item-text--${isHeroBannerPresent ? heroBannerTextColor : model.textColor}`}
                onClick={() => googleClickBreadcrumbEvent(item.text)}
              />
            ))}
          </ul>
          {isMobile && (
            <RoutableNotEditableLink
              href={model?.backButtonUrl}
              className={`nav-bar__back-button nav-bar__back-button-text--${isHeroBannerPresent ? heroBannerTextColor : model.textColor}`}
              onClick={() => googleClickBreadcrumbEvent(model?.backButtonLabel)}
            >
              <Icon iconName="arrow-simple-left" extraClassName="nav-bar__back-icon" color="white" />
              {model?.backButtonLabel}
            </RoutableNotEditableLink>
          )}
        </div>
      </nav>
    </>
  );
};

export default withSitecoreContext()(ProductBreadcrumbBar);
